import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import { GenericEvents } from '@newrelic/browser-agent/features/generic_events';
import { PageViewEvent } from '@newrelic/browser-agent/features/page_view_event';
import { Ajax } from '@newrelic/browser-agent/features/ajax';
import { JSErrors } from '@newrelic/browser-agent/features/jserrors';
import { Metrics } from '@newrelic/browser-agent/features/metrics';
import { PageViewTiming } from '@newrelic/browser-agent/features/page_view_timing';
import { Spa } from '@newrelic/browser-agent/features/spa';

declare global {
  interface Window {
    NREUM: any;
  }
}

// Create a mock agent for development environment
const mockAgent = {
  addPageAction: () => {},
  addToTrace: () => {},
  setCustomAttribute: () => {},
  setErrorHandler: () => {},
  noticeError: () => {},
  setPageViewName: () => {},
  interaction: () => {},
  recordCustomEvent: () => {}
};

// Base configuration that's common across environments
// eslint-disable-next-line camelcase
const baseConfig = {
  init: {
    // These property names are required by the NewRelic API
    /* eslint-disable camelcase */
    distributed_tracing: {
      enabled: true,
      cors_use_newrelic_header: true,
      cors_use_tracecontext_headers: true,
      allowed_origins: ['*']
    },
    privacy: {
      cookies_enabled: true
    },
    ajax: {
      deny_list: ['bam.nr-data.net']
    },
    session_replay: {
      enabled: true,
      mask_all_inputs: true
    },
    page_view_timing: {
      enabled: true
    },
    spa: {
      enabled: true,
      auto_instrument: true
    }
    /* eslint-enable camelcase */
  },
  info: {
    beacon: 'bam.nr-data.net',
    errorBeacon: 'bam.nr-data.net',
    licenseKey: 'NRBR-53f79fafe55ef465315',
    sa: 1
  }
};

// Environment-specific configurations - only int1 and prod
const envConfigs = {
  int1: {
    loaderConfig: {
      accountID: '11576',
      trustKey: '1190893',
      agentID: '1386257633',
      licenseKey: 'NRBR-53f79fafe55ef465315',
      applicationID: '1386257633'
    },
    info: {
      applicationID: '1386257633'
    }
  },
  prod: {
    loaderConfig: {
      accountID: '11576',
      trustKey: '1190893',
      agentID: '1386257646',
      licenseKey: 'NRBR-53f79fafe55ef465315',
      applicationID: '1386257646'
    },
    info: {
      applicationID: '1386257646'
    }
  }
};

// Determine environment based on URL pattern - only caring about int1 and prod
const getEnvironmentFromUrl = (): 'int1' | 'prod' | 'other' => {
  if (typeof window === 'undefined') {
    return 'other';
  }

  const url = window.location.href.toLowerCase();

  if (url.includes('int1') || url.includes('integration1')) {
    return 'int1';
  } else if (url.includes('prod') || url.includes('production')) {
    return 'prod';
  }

  // All other environments (int5, uat, dev, etc.)
  return 'other';
};

// Initialize New Relic based on environment
const initializeNewRelic = () => {
  const urlEnv = getEnvironmentFromUrl();

  // Only report for int1 and prod
  if (urlEnv === 'other') {
    return mockAgent;
  }

  // Initialize New Relic configuration
  window.NREUM = window.NREUM || {};
  const envConfig = envConfigs[urlEnv];

  // Merge base config with environment-specific config
  window.NREUM.init = baseConfig.init;
  window.NREUM.loaderConfig = envConfig.loaderConfig;
  window.NREUM.info = {
    ...baseConfig.info,
    ...envConfig.info
  };

  // Initialize New Relic Browser Agent
  return new BrowserAgent({
    init: window.NREUM.init,
    info: window.NREUM.info,
    // eslint-disable-next-line camelcase
    loader_config: window.NREUM.loaderConfig,
    features: [Metrics, GenericEvents, PageViewEvent, PageViewTiming, Ajax, JSErrors, Spa]
  });
};

// Export the initialized agent
export const newRelicAgent = initializeNewRelic();
